import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Loader2, UserCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const wsUrlFor = () => {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return null;
  const u = new URL(base);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws/chat";
  return u.toString();
};

const guestId = () => {
  let id = localStorage.getItem("pav_guest_id");
  if (!id) {
    id = `g_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem("pav_guest_id", id);
  }
  return id;
};

const ChatWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [guestName, setGuestName] = useState(localStorage.getItem("pav_guest_name") || "");
  const [guestStep, setGuestStep] = useState("name"); // 'name' | 'chat'
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  const isAdmin = user && ["super_admin", "front_office", "cashier", "production", "releasing", "accounting"].includes(user.role);
  const isGuest = !user;

  // ----- Load thread -----
  const loadCustomerThread = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chat/my-thread");
      setThread(data.thread);
      setMessages(data.messages || []);
      setUnread(0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadGuestThread = useCallback(async (name) => {
    setLoading(true);
    try {
      const { data } = await api.post("/chat/guest/start", {
        guest_id: guestId(), name,
      });
      setThread(data.thread);
      setMessages(data.messages || []);
      setUnread(0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  // ----- WebSocket (registered user only; guests use polling) -----
  useEffect(() => {
    if (!user || isAdmin) return undefined;
    let cancelled = false;
    let ws;
    const connect = () => {
      const url = wsUrlFor();
      if (!url) return;
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "message" && m.message) {
            setMessages((prev) => {
              if (prev.find((x) => x.message_id === m.message.message_id)) return prev;
              return [...prev, m.message];
            });
            if (!open && m.message.sender_role === "admin") setUnread((c) => c + 1);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { if (!cancelled) setTimeout(connect, 4000); };
    };
    connect();
    return () => { cancelled = true; try { ws && ws.close(); } catch { /* ignore */ } };
  }, [user, isAdmin, open]);

  // ----- Polling (guest only) -----
  useEffect(() => {
    if (!isGuest || !guestName || guestStep !== "chat") return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/chat/guest/poll?guest_id=${encodeURIComponent(guestId())}`);
        if (cancelled) return;
        if (data.thread) setThread(data.thread);
        if (data.messages) {
          setMessages((prev) => {
            // Merge dedup by message_id, preserve order
            const known = new Set(prev.map((x) => x.message_id));
            const fresh = data.messages.filter((x) => !known.has(x.message_id));
            return fresh.length ? [...prev, ...fresh] : prev;
          });
        }
        if (data.unread !== undefined && !open) setUnread(data.unread);
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 6000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isGuest, guestName, guestStep, open]);

  // Periodic unread refresh for registered users when closed
  useEffect(() => {
    if (!user || isAdmin || open) return undefined;
    const poll = async () => {
      try {
        const { data } = await api.get("/chat/my-thread");
        const adminUnread = (data.messages || []).filter((m) => m.sender_role === "admin" && !m.read_by_customer).length;
        if (adminUnread > 0) setUnread(adminUnread);
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [user, isAdmin, open]);

  // Open the panel & load history depending on user state
  const openPanel = async () => {
    setOpen(true); setUnread(0);
    if (user && !isAdmin) {
      loadCustomerThread();
    } else if (isGuest) {
      if (guestName) {
        setGuestStep("chat");
        loadGuestThread(guestName);
      } else {
        setGuestStep("name");
      }
    }
  };

  const confirmGuestName = (e) => {
    e?.preventDefault?.();
    const trimmed = (guestName || "").trim();
    if (!trimmed) return;
    localStorage.setItem("pav_guest_name", trimmed);
    setGuestStep("chat");
    loadGuestThread(trimmed);
  };

  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!input.trim()) return;
    setSending(true);
    const body = input.trim();
    setInput("");
    try {
      let data;
      if (user && !isAdmin) {
        ({ data } = await api.post("/chat/my-thread/send", { body }));
      } else {
        ({ data } = await api.post("/chat/guest/send", {
          guest_id: guestId(), name: guestName, body,
        }));
      }
      setMessages((prev) => prev.find((x) => x.message_id === data.message_id) ? prev : [...prev, data]);
    } catch { setInput(body); }
    finally { setSending(false); }
  };

  // Hide widget completely for admin/staff (they use /admin/chat-inbox)
  if (isAdmin) return null;

  return (
    <>
      {/* Floating launcher */}
      <button onClick={openPanel}
        data-testid="chat-launcher"
        className={`fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#0B1E3F] hover:bg-[#06B6D4] text-white shadow-2xl shadow-[#0B1E3F]/25 transition-all duration-300 flex items-center justify-center group ${open ? "scale-0 opacity-0" : "scale-100 opacity-100"}`}
        aria-label="Chat with us">
        <MessageCircle className="h-6 w-6 group-hover:scale-110 transition-transform" />
        {unread > 0 && (
          <span data-testid="chat-launcher-badge"
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#06B6D4] text-[11px] font-bold text-white flex items-center justify-center ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Chat panel */}
      <div data-testid="chat-panel"
        className={`fixed bottom-6 right-6 z-40 w-[90vw] sm:w-[380px] h-[560px] max-h-[80vh] rounded-3xl shadow-2xl bg-white border border-[#E2E8F0] flex flex-col overflow-hidden transition-all duration-300 origin-bottom-right ${open ? "scale-100 opacity-100 pointer-events-auto" : "scale-95 opacity-0 pointer-events-none"}`}>
        <div className="bg-[#0B1E3F] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full bg-[#06B6D4] flex items-center justify-center">
              <MessageCircle className="h-4 w-4" />
            </div>
            <div>
              <p className="font-semibold text-sm">Print Ad Venture</p>
              <p className="text-[11px] text-white/70 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400" /> We&apos;re here to help
              </p>
            </div>
          </div>
          <button onClick={() => setOpen(false)} data-testid="chat-close"
            className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Guest: name step */}
        {isGuest && guestStep === "name" ? (
          <form onSubmit={confirmGuestName} className="flex-1 flex flex-col items-center justify-center p-6 bg-[#F8FAFC]"
            data-testid="guest-name-form">
            <div className="h-14 w-14 rounded-full bg-white border-2 border-[#06B6D4] flex items-center justify-center mb-4">
              <UserCircle className="h-7 w-7 text-[#06B6D4]" />
            </div>
            <p className="text-sm text-neutral-600 text-center mb-4">
              Let us know your name so our team can greet you properly.
            </p>
            <input className="pav-input mb-3 w-full"
              placeholder="Your name"
              data-testid="guest-name-input"
              value={guestName} onChange={(e) => setGuestName(e.target.value)}
              required autoFocus />
            <button type="submit" className="btn-primary w-full" data-testid="guest-name-submit">
              Start chat
            </button>
            <p className="text-[10px] text-neutral-400 mt-3 text-center">
              Have an account? <a href="/login" className="text-[#06B6D4] font-semibold">Sign in</a> for order updates.
            </p>
          </form>
        ) : (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8FAFC]">
              {loading ? (
                <p className="text-center text-sm text-neutral-500"><Loader2 className="h-4 w-4 inline animate-spin" /> Loading…</p>
              ) : messages.length === 0 ? (
                <div className="text-center text-sm text-neutral-500 py-8" data-testid="chat-empty">
                  <MessageCircle className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
                  <p>Send us a message — we typically reply within a few hours.</p>
                </div>
              ) : messages.map((m) => (
                <div key={m.message_id} data-testid={`chat-msg-${m.message_id}`}
                  className={`flex ${m.sender_role === "customer" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.sender_role === "customer" ? "bg-[#0B1E3F] text-white rounded-br-sm" : "bg-white border border-[#E2E8F0] rounded-bl-sm"}`}>
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    <p className={`text-[10px] mt-1 ${m.sender_role === "customer" ? "text-white/60" : "text-neutral-400"}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={send} className="border-t border-[#E2E8F0] p-3 flex items-center gap-2 bg-white">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message…"
                className="flex-1 px-3 py-2 rounded-full border border-[#E2E8F0] focus:outline-none focus:border-[#06B6D4] text-sm"
              />
              <button type="submit" disabled={sending || !input.trim()}
                data-testid="chat-send"
                className="h-9 w-9 rounded-full bg-[#06B6D4] hover:bg-[#0891B2] text-white flex items-center justify-center disabled:opacity-50 transition-colors">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </>
        )}
      </div>
    </>
  );
};

export default ChatWidget;
